<template>
    <div
        v-click-outside="close"
        class="select"
        :class="{ 'is-opened': opened, 'is-disabled': disabled }"
        :style="{ width }"
    >
        <div class="select__field">
            <button
                ref="trigger"
                type="button"
                class="select__button"
                @click="open"
                @keydown.esc="close"
                @keydown.prevent.down="highlightNext"
                @keydown.prevent.up="highlightPrev"
                @keydown.enter.prevent="selectHighlighted"
            >
                <span v-if="modelValue && modelValue.length" class="select__value">
                    {{ trim(valueText) }}
                </span>
                <span v-else class="select__placeholder">{{ placeholder }}</span>
            </button>
            <button
                v-if="clearable && modelValue"
                tabindex="-1"
                type="button"
                class="select__clear"
                title="Очистить"
                @click.stop="clear"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="9" height="9">
                    <path
                        d="M505.943 6.058c-8.077-8.077-21.172-8.077-29.249 0L6.058 476.693c-8.077 8.077-8.077 21.172 0 29.249A20.612 20.612 0 0020.683 512a20.614 20.614 0 0014.625-6.059L505.943 35.306c8.076-8.076 8.076-21.171 0-29.248z"
                    />
                    <path
                        d="M505.942 476.694L35.306 6.059c-8.076-8.077-21.172-8.077-29.248 0-8.077 8.076-8.077 21.171 0 29.248l470.636 470.636a20.616 20.616 0 0014.625 6.058 20.615 20.615 0 0014.624-6.057c8.075-8.078 8.075-21.173-.001-29.25z"
                    />
                </svg>
            </button>
            <input
                :id="context.name || $attrs.name"
                :value="modelValue"
                tabindex="-1"
                type="text"
                :required="context.required || required"
                :name="context.name || $attrs.name"
                class="select__hidden-input"
            />
        </div>
        <transition name="fade">
            <div
                v-show="opened"
                ref="dropdown"
                class="select__dropdown"
                :class="{ 'is-visible': opened }"
            >
                <div class="select__filter-wrap">
                    <input
                        v-if="withFilter"
                        ref="filter"
                        v-model="filter"
                        :placeholder="filterPlaceholder"
                        class="select__filter"
                        type="text"
                        @keydown.esc="filteresc"
                        @keydown.prevent.down="highlightNext"
                        @keydown.prevent.up="highlightPrev"
                        @keydown.enter.prevent="selectHighlighted"
                    />
                    <button
                        type="button"
                        class="button button--blank select__select-all"
                        @click="toggleAll"
                    >
                        {{ allSelected ? $t('misc.unselectAll') : $t('misc.selectAll') }}
                    </button>
                </div>
                <ul ref="list" class="select__list">
                    <slot name="list-prepend" :close="close" />
                    <li
                        v-for="(option, index) in filteredOptions"
                        :key="index"
                        class="select__option"
                        :class="{
                            'is-active': isOptionActive(option),
                            'is-highlighted': index === highlightedIndex
                        }"
                        @click="select(option)"
                    >
                        <Checkbox
                            :model-value="isOptionSelected(option)"
                            theme="inverted"
                            size="small"
                            :required="false"
                            @update:model-value="select(option)"
                        >
                            <slot :item="option" :close="close">
                                {{ getOptionLabel(option) }}
                            </slot>
                        </Checkbox>
                    </li>
                    <slot name="list-append" :close="close" />
                    <li v-if="paginate && !filter && canPaginate">
                        <div class="select__more">
                            <button v-t="loadMoreText" class="link" @click.prevent="loadMore" />
                        </div>
                    </li>
                </ul>
                <div
                    v-show="emptyText && !filteredOptions.length && opened"
                    class="select__option select__option--empty"
                >
                    {{ emptyText }}
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import i18n from '@speechki-frontend/core/i18n/i18n';
import Popper from 'popper.js';
import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';

import Checkbox from '../Checkbox/Checkbox';

const noop = () => {};
const empty = () => ({});

export default {
    name: 'MultiSelect',
    components: {
        Checkbox
    },
    directives: {
        clickOutside
    },
    emits: ['update:modelValue', 'closed', 'search'],
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        options: {
            type: Array,
            default() {
                return [];
            }
        },
        emitProp: {
            type: String,
            default: null
        },
        viewLabel: {
            type: String,
            default: null
        },
        placeholder: {
            type: String,
            default() {
                return i18n.global.t('misc.select');
            }
        },
        filterPlaceholder: {
            type: String,
            default() {
                return i18n.global.t('misc.search');
            }
        },
        textThreshold: {
            type: Number,
            default: 26
        },
        width: {
            type: [String, Number],
            default: 'auto'
        },
        required: {
            type: Boolean,
            default: false
        },
        emptyText: {
            type: String,
            default: ''
        },
        withFilter: {
            type: Boolean,
            default: false
        },
        formValueText: {},
        clearable: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        paginate: {
            type: Boolean,
            default: false
        },
        paginateAmount: {
            type: Number,
            default: 100
        },
        asyncSearch: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        loadMoreText: {
            type: String,
            default: 'misc.loadmore'
        },
        allSelectable: {
            type: Boolean,
            default: false
        }
    },

    inject: {
        context: {
            default: empty
        },
        registerControl: {
            default: () => noop
        }
    },

    data() {
        return {
            opened: false,
            filter: '',
            popper: undefined,
            highlightedIndex: undefined,
            page: 1,
            timeout: null
        };
    },
    computed: {
        valueText() {
            let items = undefined;

            if (this.emitProp) {
                items = this.options.filter(item => this.modelValue.includes(item[this.emitProp]));
            } else {
                items = this.options.filter(item => this.modelValue.includes(item));
            }

            if (items && items.length) {
                if (this.formValueText) {
                    return items.reduce((prev, acc, index) => {
                        return (
                            prev +
                            `${this.formValueText(acc)}${
                                index < this.modelValue.length - 1 ? ', ' : ''
                            } `
                        );
                    }, '');
                }

                if (this.viewLabel) {
                    return items.reduce((acc, prev, index) => {
                        return (
                            prev +
                            `${acc[this.viewLabel]}${index < this.modelValue.length - 1 ? ', ' : ''} `
                        );
                    }, '');
                }

                return items.reduce((acc, prev, index) => {
                    return prev + `${acc}${index < this.modelValue.length - 1 ? ', ' : ''} `;
                }, '');
            }

            return '';
        },
        filteredOptions() {
            if (this.asyncSearch) {
                return this.options;
            }

            let result = this.options.slice();

            if (this.filter) {
                const filterLower = this.filter.toLowerCase();
                result = result.filter(option => {
                    if (this.formValueText) {
                        return this.formValueText(option)
                            .toLowerCase()
                            .includes(filterLower);
                    } else if (this.viewLabel) {
                        return option[this.viewLabel].toLowerCase().includes(filterLower);
                    } else {
                        return option.toLowerCase().includes(filterLower);
                    }
                });
            }

            if (this.paginate) {
                result = result.slice(0, this.page * this.paginateAmount);
            }

            return result;
        },
        canPaginate() {
            return this.paginate && this.page * this.paginateAmount < this.options.length;
        },
        allSelected() {
            let selected = this.filteredOptions.map(option => {
                if (this.emitProp) {
                    return option[this.emitProp];
                }

                return this.option;
            });

            return JSON.stringify(selected) === JSON.stringify(this.modelValue);
        }
    },
    watch: {
        filter() {
            clearTimeout(this.timeout);

            this.timeout = setTimeout(() => {
                this.$emit('search', this.filter);
            }, 250);
        }
    },

    mounted() {
        this.registerControl(this);
    },

    beforeUnmount() {
        if (this.popper) {
            this.popper.destroy();
        }
    },

    methods: {
        isOptionSelected(option) {
            if (this.emitProp) {
                return this.modelValue.includes(option[this.emitProp]);
            }

            return this.modelValue.includes(option);
        },
        open() {
            if (this.opened) return this.close();

            this.opened = true;
            this.$nextTick(() => {
                this.setupPopper();
                if (this.withFilter) {
                    this.$refs.filter.focus();
                }
                this.scrollToHighlighted();
            });
        },
        setupPopper() {
            if (this.popper === undefined) {
                this.popper = new Popper(this.$refs.trigger, this.$refs.dropdown, {
                    placement: 'bottom',
                    modifiers: {
                        offset: {
                            enabled: true,
                            offset: '0, 10'
                        }
                    }
                });
            } else {
                this.popper.scheduleUpdate();
            }
        },
        toggle() {
            this.opened = !this.opened;
        },
        close() {
            if (!this.opened) return;

            this.opened = false;

            if (this.withFilter) {
                this.filter = '';
            }
            this.$emit('closed');
        },
        trim(text) {
            if (text.length < this.textThreshold) return text;
            return text.slice(0, this.textThreshold) + '...';
        },
        select(option) {
            if (this.emitProp) {
                if (this.modelValue.includes(option[this.emitProp])) {
                    let result = this.modelValue.filter(item => item !== option[this.emitProp]);
                    this.$emit('update:modelValue', result);
                } else {
                    this.$emit('update:modelValue', [...this.modelValue, option[this.emitProp]]);
                }
            } else {
                if (this.modelValue.includes(option[this.emitProp])) {
                    let result = this.modelValue.filter(item => item !== option);
                    this.$emit('update:modelValue', result);
                } else {
                    this.$emit('update:modelValue', [...this.modelValue, option]);
                }
            }
        },
        toggleAll() {
            let selected = [];

            if (!this.allSelected) {
                selected = this.filteredOptions.map(option => {
                    if (this.emitProp) {
                        return option[this.emitProp];
                    }

                    return this.option;
                });
            }
            this.$emit('update:modelValue', selected);
        },
        clear() {
            this.$emit('update:modelValue', null);
        },
        isOptionActive(option) {
            if (this.emitProp) {
                return this.modelValue === option[this.emitProp];
            }
            return this.modelValue === option;
        },
        getOptionLabel(option) {
            if (this.viewLabel) {
                return option[this.viewLabel];
            }

            return option;
        },
        highlight(index) {
            this.highlightedIndex = index;

            if (this.highlightedIndex < 0) {
                this.highlightedIndex = this.filteredOptions.length - 1;
            }

            if (this.highlightedIndex > this.filteredOptions.length - 1) {
                this.highlightedIndex = 0;
            }

            this.$refs.list.children[this.highlightedIndex].scrollIntoView({ block: 'nearest' });
        },
        highlightNext() {
            if (this.highlightedIndex === undefined) {
                this.highlight(0);
                return;
            }
            this.highlight(this.highlightedIndex + 1);
        },
        highlightPrev() {
            if (this.highlightedIndex === undefined) {
                this.highlight(0);
                return;
            }
            this.highlight(this.highlightedIndex - 1);
        },
        selectByIndex() {
            this.select(this.filteredOptions[this.highlightedIndex][this.prop]);
            this.close();
        },
        scrollToHighlighted() {
            if (this.$refs.list.children.length && this.highlightedIndex) {
                this.$refs.list.children[this.highlightedIndex].scrollIntoView({
                    block: 'nearest'
                });
            }
        },
        selectHighlighted() {
            this.select(this.filteredOptions[this.highlightedIndex]);
        },
        loadMore() {
            this.page = this.page + 1;
        }
    }
};
</script>
